import React from 'react';
import { BouncingDots } from '../../../shared/components/BouncingDots';

interface StatCardProps {
  label: string;
  value: React.ReactNode;
  icon: 'pending' | 'approved' | 'producers' | 'volume';
  color?: string;
  gradient?: boolean;
}

export const StatCard: React.FC<StatCardProps> = ({ label, value, icon, color = '#006837', gradient = false }) => {
  const displayValue = value === '...' ? <BouncingDots isWhite={gradient} /> : value;

  if (gradient) {
    return (
      <div className="rounded-xl p-5 flex items-start justify-between hover:shadow-lg transition cursor-default" style={{ background: 'linear-gradient(135deg, #006837 0%, #00813F 100%)', borderTop: '2px solid #5BBA47' }}>
        <div>
          <div className="text-3xl font-extrabold tracking-tight text-white">{displayValue}</div>
          <div className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.85)' }}>{label}</div>
        </div>
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.18)' }}>
          {renderIcon(icon, 'white')}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-border rounded-xl p-5 flex items-start justify-between hover:shadow-md transition cursor-default" style={{ borderTop: '2px solid #006837' }}>
      <div>
        <div className="text-3xl font-extrabold tracking-tight" style={{ color: '#212121' }}>{displayValue}</div>
        <div className="text-sm text-muted-foreground mt-1">{label}</div>
      </div>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: getBgColor(icon) }}>
        {renderIcon(icon)}
      </div>
    </div>
  );
};

function getBgColor(icon: string) {
  switch (icon) {
    case 'pending': return 'rgba(245, 158, 11, 0.12)';
    case 'approved': return 'rgba(91, 186, 71, 0.15)';
    case 'producers': return 'rgba(0, 104, 55, 0.10)';
    default: return 'rgba(0, 104, 55, 0.10)';
  }
}

function renderIcon(icon: string, strokeColor?: string) {
  switch (icon) {
    case 'pending':
      return (
        <svg className="w-5 h-5" fill="none" stroke={strokeColor || "#D97706"} strokeWidth="2.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      );
    case 'approved':
      return (
        <svg className="w-5 h-5" fill="none" stroke={strokeColor || "#006837"} strokeWidth="2.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
        </svg>
      );
    case 'producers':
      return (
        <svg className="w-5 h-5" fill="none" stroke={strokeColor || "#006837"} strokeWidth="2.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
        </svg>
      );
    case 'volume':
      return (
        <svg className="w-5 h-5" fill="none" stroke={strokeColor || "white"} strokeWidth="2.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
      );
    default:
      return null;
  }
}
